import { supabase as _supabase } from './supabase';

const supabase = _supabase;
const db = _supabase as any;

export interface CoachStats {
  totalRespondents: number;
  completedAssessments: number;
  pendingAssessments: number;
  completionRate: number;
  aiAnalysesGenerated: number;
  retakesCompleted: number;
}

export interface CoachMonthlyAssessment {
  month: string;
  label: string;
  count: number;
}

export interface CharacteristicAverage {
  characteristicId: string;
  name: string;
  category: 'being' | 'doing' | 'having';
  average: number;
}

export interface HarmonicAverage {
  levelId: number;
  name: string;
  average: number;
}

export interface CoachRecentCompletion {
  id: string;
  respondentName: string;
  completedAt: string;
  hasAiAnalysis: boolean;
}

export interface RespondentBreakdown {
  completed: number;
  pending: number;
  withAiAnalysis: number;
  withRetakes: number;
}

export async function fetchCoachId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('coaches')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return data?.id || null;
}

export async function fetchCoachStats(coachId: string): Promise<CoachStats> {
  const [respondentsRes, resultsRes, aiRes, retakesRes] = await Promise.all([
    supabase
      .from('respondents')
      .select('user_id', { count: 'exact' })
      .eq('coach_id', coachId),
    supabase
      .from('results')
      .select('user_id', { count: 'exact' })
      .eq('coach_id', coachId),
    supabase
      .from('results')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', coachId)
      .not('ai_analysis', 'is', null),
    supabase
      .from('results')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', coachId)
      .gt('version', 1),
  ]);

  const totalRespondents = respondentsRes.count || 0;
  const completedAssessments = resultsRes.count || 0;

  const respondentUserIds = (respondentsRes.data || []).map(r => r.user_id);
  const resultUserIds = new Set((resultsRes.data || []).map(r => r.user_id));
  const completedRespondents = respondentUserIds.filter(id => resultUserIds.has(id)).length;
  const pendingAssessments = totalRespondents - completedRespondents;

  return {
    totalRespondents,
    completedAssessments,
    pendingAssessments,
    completionRate: totalRespondents > 0 ? Math.round((completedRespondents / totalRespondents) * 100) : 0,
    aiAnalysesGenerated: aiRes.count || 0,
    retakesCompleted: retakesRes.count || 0,
  };
}

export async function fetchCoachAssessmentsOverTime(coachId: string): Promise<CoachMonthlyAssessment[]> {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  sixMonthsAgo.setDate(1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  const { data: results } = await supabase
    .from('results')
    .select('completed_at')
    .eq('coach_id', coachId)
    .gte('completed_at', sixMonthsAgo.toISOString())
    .order('completed_at', { ascending: true });

  const monthMap: Record<string, number> = {};
  const months: { month: string; label: string }[] = [];

  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short' });
    months.push({ month: key, label });
    monthMap[key] = 0;
  }

  (results || []).forEach(r => {
    if (r.completed_at) {
      const d = new Date(r.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthMap[key] !== undefined) {
        monthMap[key]++;
      }
    }
  });

  return months.map(m => ({
    month: m.month,
    label: m.label,
    count: monthMap[m.month],
  }));
}

export async function fetchCoachCharacteristicAverages(coachId: string): Promise<CharacteristicAverage[]> {
  const { data: results } = await supabase
    .from('results')
    .select('scores')
    .eq('coach_id', coachId);

  if (!results || results.length === 0) return [];

  const characteristicMap: Record<string, { name: string; category: 'being' | 'doing' | 'having'; total: number; count: number }> = {};

  const idToMeta: Record<string, { name: string; category: 'being' | 'doing' | 'having' }> = {
    ambition: { name: 'Ambition', category: 'being' },
    boldness: { name: 'Boldness', category: 'being' },
    integrity: { name: 'Integrity', category: 'being' },
    positiveOutlook: { name: 'Positive Outlook', category: 'being' },
    passion: { name: 'Dynamic Energy', category: 'doing' },
    sociability: { name: 'Sociability', category: 'doing' },
    communication: { name: 'Communication', category: 'doing' },
    wisdom: { name: 'Judgment', category: 'doing' },
    learning: { name: 'Learning', category: 'having' },
    creativity: { name: 'Creativity', category: 'having' },
    flexibility: { name: 'Adaptability', category: 'having' },
    visionaryThinking: { name: 'Visionary Thinking', category: 'having' },
  };

  results.forEach(r => {
    const scores = r.scores as Array<{ characteristicId: string; normalized: number }>;
    if (!Array.isArray(scores)) return;

    scores.forEach(s => {
      const meta = idToMeta[s.characteristicId];
      if (!meta) return;

      if (!characteristicMap[s.characteristicId]) {
        characteristicMap[s.characteristicId] = { ...meta, total: 0, count: 0 };
      }
      characteristicMap[s.characteristicId].total += s.normalized;
      characteristicMap[s.characteristicId].count++;
    });
  });

  const order = Object.keys(idToMeta);

  return order
    .filter(id => characteristicMap[id])
    .map(id => ({
      characteristicId: id,
      name: characteristicMap[id].name,
      category: characteristicMap[id].category,
      average: Math.round(characteristicMap[id].total / characteristicMap[id].count),
    }));
}

export async function fetchCoachHarmonicAverages(coachId: string): Promise<HarmonicAverage[]> {
  const { data: results } = await supabase
    .from('results')
    .select('harmonic_scores')
    .eq('coach_id', coachId);

  if (!results || results.length === 0) return [];

  const levelNames: Record<number, string> = {
    1: 'Apathy', 2: 'Hopelessness', 3: 'Grief', 4: 'Fear',
    5: 'Passive Hostility', 6: 'Open Anger', 7: 'Antagonism',
    8: 'Rational Neutrality', 9: 'Interest', 10: 'Optimism',
    11: 'Enthusiasm', 12: 'Exhilaration',
  };

  const levelMap: Record<number, { total: number; count: number }> = {};

  results.forEach(r => {
    const scores = r.harmonic_scores as Array<{ levelId: number; normalized: number }>;
    if (!Array.isArray(scores)) return;

    scores.forEach(s => {
      if (!levelMap[s.levelId]) {
        levelMap[s.levelId] = { total: 0, count: 0 };
      }
      levelMap[s.levelId].total += s.normalized;
      levelMap[s.levelId].count++;
    });
  });

  return Array.from({ length: 12 }, (_, i) => i + 1)
    .filter(id => levelMap[id])
    .map(id => ({
      levelId: id,
      name: levelNames[id],
      average: Math.round(levelMap[id].total / levelMap[id].count),
    }));
}

export async function fetchCoachRecentCompletions(coachId: string): Promise<CoachRecentCompletion[]> {
  const { data: results } = await supabase
    .from('results')
    .select('id, user_id, completed_at, ai_analysis, users!results_user_id_fkey(full_name, email)')
    .eq('coach_id', coachId)
    .order('completed_at', { ascending: false })
    .limit(8);

  return (results || []).map(r => ({
    id: r.id,
    respondentName: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
    completedAt: r.completed_at || '',
    hasAiAnalysis: !!r.ai_analysis,
  }));
}

export async function fetchCoachRespondentBreakdown(coachId: string): Promise<RespondentBreakdown> {
  const { data: respondents } = await supabase
    .from('respondents')
    .select('user_id')
    .eq('coach_id', coachId);

  if (!respondents || respondents.length === 0) {
    return { completed: 0, pending: 0, withAiAnalysis: 0, withRetakes: 0 };
  }

  const userIds = respondents.map(r => r.user_id);

  const { data: results } = await supabase
    .from('results')
    .select('user_id, ai_analysis, version')
    .eq('coach_id', coachId);

  const completedUserIds = new Set((results || []).map(r => r.user_id));
  const withAi = new Set((results || []).filter(r => r.ai_analysis).map(r => r.user_id));
  const withRetakes = new Set((results || []).filter(r => (r.version || 1) > 1).map(r => r.user_id));

  return {
    completed: completedUserIds.size,
    pending: userIds.length - completedUserIds.size,
    withAiAnalysis: withAi.size,
    withRetakes: withRetakes.size,
  };
}

export interface ClientProgressActivity {
  respondentId: string;
  userId: string;
  respondentName: string;
  type: 'check_in' | 'goal_completed' | 'milestone' | 'task_done' | 'journal';
  label: string;
  occurredAt: string;
}

export async function fetchCoachClientRecentActivity(coachId: string): Promise<ClientProgressActivity[]> {
  const { data: respondents } = await supabase
    .from('respondents')
    .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
    .eq('coach_id', coachId);

  if (!respondents || respondents.length === 0) return [];

  const since = new Date();
  since.setDate(since.getDate() - 14);
  const sinceIso = since.toISOString();

  const userIds = respondents.map(r => r.user_id);
  const nameMap = new Map<string, { id: string; name: string }>(respondents.map(r => [
    r.user_id,
    { id: r.id, name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown' },
  ]));

  const [checkIns, goalsCompleted, milestones, tasksDone, journals] = await Promise.all([
    db.from('check_ins').select('user_id, checked_in_at, energy_level, mood_level, focus_level').in('user_id', userIds).gte('checked_in_at', sinceIso).order('checked_in_at', { ascending: false }).limit(50),
    db.from('goals').select('user_id, title, completed_at').in('user_id', userIds).eq('status', 'completed').gte('completed_at', sinceIso).order('completed_at', { ascending: false }).limit(20),
    db.from('milestones').select('user_id, title, achieved_at').in('user_id', userIds).gte('achieved_at', sinceIso).order('achieved_at', { ascending: false }).limit(20),
    db.from('tasks').select('user_id, title, completed_at').in('user_id', userIds).eq('status', 'done').gte('completed_at', sinceIso).order('completed_at', { ascending: false }).limit(20),
    db.from('journal_entries').select('user_id, title, created_at').in('user_id', userIds).gte('created_at', sinceIso).order('created_at', { ascending: false }).limit(20),
  ]);

  const activities: ClientProgressActivity[] = [];

  (checkIns.data || []).forEach((r: any) => {
    const info = nameMap.get(r.user_id);
    if (!info) return;
    const avg = Math.round((r.energy_level + r.mood_level + r.focus_level) / 3);
    activities.push({ respondentId: info.id, userId: r.user_id, respondentName: info.name, type: 'check_in', label: `Checked in — avg score ${avg}/10`, occurredAt: r.checked_in_at });
  });

  (goalsCompleted.data || []).forEach((r: any) => {
    const info = nameMap.get(r.user_id);
    if (!info) return;
    activities.push({ respondentId: info.id, userId: r.user_id, respondentName: info.name, type: 'goal_completed', label: `Completed goal: ${r.title}`, occurredAt: r.completed_at });
  });

  (milestones.data || []).forEach((r: any) => {
    const info = nameMap.get(r.user_id);
    if (!info) return;
    activities.push({ respondentId: info.id, userId: r.user_id, respondentName: info.name, type: 'milestone', label: `Milestone: ${r.title}`, occurredAt: r.achieved_at });
  });

  (tasksDone.data || []).forEach((r: any) => {
    const info = nameMap.get(r.user_id);
    if (!info) return;
    activities.push({ respondentId: info.id, userId: r.user_id, respondentName: info.name, type: 'task_done', label: `Completed task: ${r.title}`, occurredAt: r.completed_at });
  });

  (journals.data || []).forEach((r: any) => {
    const info = nameMap.get(r.user_id);
    if (!info) return;
    activities.push({ respondentId: info.id, userId: r.user_id, respondentName: info.name, type: 'journal', label: `Journal: ${r.title}`, occurredAt: r.created_at });
  });

  return activities.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()).slice(0, 30);
}
